import {
  draftBindingSchemaDrafts,
  draftSchemaDraft,
  splitLegacySchemaDraft,
  type BindingSchemaDrafts,
  type BindingSchemaKey,
  type SchemaDraft,
} from './schema-draft'
import {editorBlocksToHMBlockNodes} from '@seed-hypermedia/client/editorblock-to-hmblock'
import {EditorBlock, EditorQueryBlock} from '@seed-hypermedia/client/editor-types'
import {
  entityQueryPathToHmIdPath,
  HMBlockChildrenType,
  HMBlockNode,
  HMDocument,
  HMDraft,
  HMMetadata,
  HMNavigationItem,
  UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {hmBlocksToEditorContent} from '@seed-hypermedia/client/hmblock-to-editorblock'
import {nanoid} from 'nanoid'
import {assign, emit, enqueueActions, fromPromise, raise, setup, spawnChild, StateFrom} from 'xstate'
import {collectChildDraftIds} from '../utils/child-draft-refs'
import {
  getDirectChildrenLosingReferences,
  getRemovedChildReferenceTargets,
  hasSelfQueryBlock,
} from '../utils/document-card-cleanup'
import {
  reconcileChildRemovalIntent,
  type ConfirmChildDeletionInput,
  type ConfirmedChildDeletion,
} from '../utils/confirmed-child-deletion'
import {createBlocksMap, isBlocksEqual} from '../utils/document-changes'
import {rebaseDocumentReferenceDraft} from '../utils/document-reference-rebase'

/** Reconciles a maintenance notification with unsaved editor content, never silently replacing conflicting edits. */
export function reconcileLiveDocumentMaintenance(
  context: DocumentMachineContext,
  event: Extract<DocumentMachineEvent, {type: 'draft.externallyModified'}>,
  liveContent: HMBlockNode[] | null,
): Partial<DocumentMachineContext> {
  if (event.source !== 'document-card-cleanup' || event.draftId !== context.draftId || !event.content) return {}
  if (event.maintenanceRevision !== undefined && event.maintenanceRevision <= (context.maintenanceRevision ?? 0))
    return {}
  if (
    liveContent &&
    event.maintenanceRevision !== undefined &&
    event.maintenanceRevision > (context.maintenanceRevision ?? 0) + 1
  ) {
    throw new Error('Live edits missed intermediate maintenance snapshots; review the saved draft before reloading')
  }
  const mine = liveContent ?? event.content
  const previous = event.previousContent
  if (liveContent && !previous && JSON.stringify(mine) !== JSON.stringify(event.content)) {
    throw new Error('Cannot reconcile live edits without the previous draft snapshot')
  }
  const result =
    previous && liveContent
      ? rebaseDocumentReferenceDraft(event, previous, mine, event.content)
      : {content: event.content, mineTouchedIds: event.mineTouchedIds ?? context.mineTouchedIds}
  const baseline = event.publishedDocument?.content ?? event.baseBlocks ?? context.baseBlocks
  return {
    maintenanceRevision: event.maintenanceRevision ?? context.maintenanceRevision,
    draftContent: result.content,
    removedChildDocumentIds: reconcileChildRemovalIntent(
      event,
      Array.from(new Set([...context.removedChildDocumentIds, ...(event.removedChildDocumentIds ?? [])])),
    ),
    metadata: context.metadata,
    deps: event.deps ?? context.deps,
    baseBlocks: baseline,
    mineTouchedIds: Array.from(
      new Set([...context.mineTouchedIds, ...(event.mineTouchedIds ?? []), ...result.mineTouchedIds]),
    ),
    document: event.publishedDocument ?? context.document,
    publishedVersion: event.publishedDocument?.version ?? context.publishedVersion,
    editorBaseline: baseline ? hmBlocksToEditorContent(baseline, {childrenType: 'Group'}) : context.editorBaseline,
    hasChangedWhileSaving:
      context.hasChangedWhileSaving || JSON.stringify(result.content) !== JSON.stringify(event.content),
  }
}

const DOCUMENT_EMBED_CLEANUP_LOG_PREFIX = '[Document embed cleanup]'

function isDocumentEmbedCleanupLoggingEnabled() {
  return Boolean((globalThis as any).__SEED_DOCUMENT_EMBED_CLEANUP_LOGS__)
}

function documentEmbedCleanupInfo(...args: unknown[]) {
  if (isDocumentEmbedCleanupLoggingEnabled()) console.info(...args)
}

function documentEmbedCleanupError(...args: unknown[]) {
  if (isDocumentEmbedCleanupLoggingEnabled()) console.error(...args)
}

function getTopLevelBlockCount(blocks: unknown[] | null | undefined) {
  return Array.isArray(blocks) ? blocks.length : 0
}

/** Type derived from a document's top-level content. */
export type DerivedDocumentType = 'document' | 'collection'

/** Creates the canonical query block used by new document Collections. */
export function createDefaultCollectionQueryBlock(blockId: string): EditorQueryBlock {
  return {
    id: blockId,
    type: 'query',
    props: {
      style: 'Table',
      columnCount: '3',
      queryLimit: '',
      queryIncludes: JSON.stringify([{space: '', path: '', mode: 'Children'}]),
      querySort: JSON.stringify([{term: 'updated', reverse: true}]),
      defaultOpen: 'true',
      tableConfig: '',
    },
    content: [],
    children: [],
  }
}

type QueryInclude = {
  space?: unknown
  path?: unknown
  [key: string]: unknown
}

/** Whether draft data may overlay published content for the current route version. */
export function shouldAllowDraftOverlay(isLatest: boolean, routeVersion: string | null | undefined) {
  return isLatest || !routeVersion
}

function includeTargetsDocument(include: QueryInclude, documentId: UnpackedHypermediaId) {
  if (include.space !== documentId.uid) return false
  const includePath = entityQueryPathToHmIdPath(typeof include.path === 'string' ? include.path : '')
  return includePath.join('/') === (documentId.path ?? []).join('/')
}

function includeIsEmpty(include: QueryInclude) {
  const space = typeof include.space === 'string' ? include.space : ''
  const path = typeof include.path === 'string' ? include.path : ''
  return space === '' && path === ''
}

/** Derives whether top-level content represents a normal document or a Collection. */
export function deriveDocumentType(
  content: HMBlockNode[] | EditorBlock[] | null | undefined,
  documentId: UnpackedHypermediaId,
): DerivedDocumentType {
  if (!Array.isArray(content) || content.length !== 1) return 'document'

  const root = content[0]
  let includes: unknown
  if (root && 'block' in root) {
    if (root.block.type !== 'Query') return 'document'
    includes = root.block.attributes?.query?.includes
  } else {
    if (!root || root.type !== 'query') return 'document'
    const rawIncludes = root.props.queryIncludes
    if (!rawIncludes) return 'collection'
    try {
      includes = JSON.parse(rawIncludes)
    } catch {
      return 'document'
    }
  }

  if (includes == null) return 'collection'
  if (!Array.isArray(includes)) return 'document'
  if (includes.length === 0) return 'collection'
  if (includes.length !== 1) return 'document'
  const include = includes[0]
  if (!include || typeof include !== 'object') return 'document'
  const queryInclude = include as QueryInclude
  return includeIsEmpty(queryInclude) || includeTargetsDocument(queryInclude, documentId) ? 'collection' : 'document'
}

function retargetQueryBlock(block: EditorBlock, fromId: UnpackedHypermediaId, toId: UnpackedHypermediaId) {
  if (block.type !== 'query') return block
  const rawIncludes = block.props.queryIncludes
  if (!rawIncludes) return block

  let includes: unknown
  try {
    includes = JSON.parse(rawIncludes)
  } catch {
    return block
  }
  if (!Array.isArray(includes)) return block

  const targetPath = (toId.path ?? []).join('/')
  let changed = false
  const nextIncludes = includes.map((include) => {
    if (!include || typeof include !== 'object') return include
    const queryInclude = include as QueryInclude
    if (!includeTargetsDocument(queryInclude, fromId)) return include
    changed = true
    return {
      ...queryInclude,
      space: toId.uid,
      path: targetPath,
    }
  })

  if (!changed) return block
  return {
    ...block,
    props: {
      ...block.props,
      queryIncludes: JSON.stringify(nextIncludes),
    },
  } as EditorBlock
}

/**
 * Retarget query-block includes that still point at the draft placeholder
 * document to the final published document id.
 */
export function retargetQueryBlockIncludesForPublish(
  blocks: EditorBlock[],
  fromId: UnpackedHypermediaId,
  toId: UnpackedHypermediaId,
): EditorBlock[] {
  if (fromId.uid === toId.uid && (fromId.path ?? []).join('/') === (toId.path ?? []).join('/')) return blocks

  let changed = false
  const nextBlocks = blocks.map((block) => {
    const retargetedBlock = retargetQueryBlock(block, fromId, toId)
    let nextBlock = retargetedBlock
    if (block.children?.length) {
      const nextChildren = retargetQueryBlockIncludesForPublish(block.children, fromId, toId)
      if (nextChildren !== block.children) {
        nextBlock = {...retargetedBlock, children: nextChildren} as EditorBlock
      }
    }
    if (nextBlock !== block) changed = true
    return nextBlock
  })

  return changed ? nextBlocks : blocks
}

// -- Types --

/**
 * Platform-agnostic interface for reading the active editor instance.
 * Both web and desktop create an accessor conforming to this shape,
 * which actors use to read blocks and cursor position.
 */
export type EditCursorPosition = number | 'end'

export interface EditorAccessor {
  /** Read the editor's current top-level blocks. */
  getTopLevelBlocks(): EditorBlock[] | null
  /** Optional cursor offset for restoring on reload. */
  getCursorPosition?: () => number | null
}

/** Input provided when creating the document machine actor. */
export type DocumentMachineInput = {
  documentId: UnpackedHypermediaId
  canEdit: boolean
  isLatest?: boolean
  routeVersion?: string | null
  reservedDraftId?: string
  existingDraftId?: string
  editUid?: string
  editPath?: string[]
  locationUid?: string
  locationPath?: string[]
  deps?: string[]
  maintenanceRevision?: number
  signingAccountId?: string
  publishAccountUid?: string
}

/**
 * Pending rebase state recorded during editing.
 * - `auto`: non-overlapping incoming changes can be merged silently.
 * - `conflict`: at least one block was touched on both sides — Phase B handles picks.
 */
export type PendingRebase =
  | {kind: 'auto'; author: string | null}
  | {kind: 'conflict'; conflictedBlockIds: string[]; author: string | null}

// Desktop draft snapshots use editor blocks; reference analysis consumes published block nodes.
function getChildDeletionContent(context: DocumentMachineContext): HMBlockNode[] {
  const content = context.draftContent ?? context.baseBlocks ?? context.document?.content ?? []
  return content.length && !('block' in content[0]!)
    ? editorBlocksToHMBlockNodes(content as unknown as EditorBlock[])
    : content
}

function getChildDeletionCandidates(context: DocumentMachineContext) {
  const before = [
    ...(context.baseBlocks ?? context.document?.content ?? []),
    ...context.removedChildDocumentIds.map(
      (link) => ({block: {id: link, type: 'Embed', link, attributes: {view: 'Card'}}, children: []}) as HMBlockNode,
    ),
  ]
  return getDirectChildrenLosingReferences(context.documentId, before, getChildDeletionContent(context))
}

/** Full context managed by the machine. */
export type DocumentMachineContext = {
  documentId: UnpackedHypermediaId
  /** Type derived from the effective published or draft content. */
  documentType: DerivedDocumentType
  draftId: string | null
  document: HMDocument | null
  metadata: HMDraft['metadata']
  deps: string[]
  /** Scope awaiting explicit consent; never reused after leaving publishing. */
  confirmedChildDeletions: ConfirmedChildDeletion[]
  /** Authored removals retained until publish/discard; system reconciliation never adds intent. */
  removedChildDocumentIds: string[]
  /** Failure while safely inspecting the proposed destructive scope. */
  childDeletionError: string | null
  publishedVersion: string | null
  isLatestVersion: boolean
  navigation: HMNavigationItem[] | undefined
  locationUid: string
  locationPath: string[]
  editUid: string
  editPath: string[]
  canEdit: boolean
  routeVersion: string | null
  hasChangedWhileSaving: boolean
  draftCreated: boolean
  /** True when machine should enter editing after loading (existing or route-reserved draft). Cleared after first use. */
  shouldAutoEdit: boolean
  signingAccountId: string | null
  publishAccountUid: string | null
  /** True once the document query has resolved during loading. */
  documentReady: boolean
  /** True once the draft query has resolved during loading (either found or not found). */
  draftReady: boolean
  /** Draft content loaded on init. Only set during loading — not updated on autosave. */
  draftContent: HMBlockNode[] | null
  /** Cursor position saved in the draft file; restored when re-entering editing after reload. */
  draftCursorPosition: number | null
  /** Cursor requested by the edit.start event that entered or refocused editing. */
  pendingEditCursorPosition: EditCursorPosition | null
  /** Published content in editor-block format. Baseline for unpublished-change diffs. */
  editorBaseline: EditorBlock[] | null
  /** Snapshot of the published blocks taken on entry to editing. Used as the three-way merge base. */
  baseBlocks: HMBlockNode[] | null
  /** Block IDs touched by the local user since edit start (from ProseMirror tr listener). */
  mineTouchedIds: string[]
  /** Last observed local maintenance epoch for optimistic autosave writes. */
  maintenanceRevision: number
  /** Full remote document stashed on remoteUpdate while editing. Classification runs outside the machine. */
  pendingRemoteDocument: HMDocument | null
  /** Classification of pending remote update: auto-mergeable or conflict. */
  pendingRebase: PendingRebase | null
  /**
   * Optional path picked by the user in the publish popover, captured on
   * `publish.start` and forwarded to the publishDocument actor input. Cleared
   * on cleanup so it does not leak to a subsequent publish.
   */
  pendingPathOverride: string[] | null
  /** Child draft ids currently referenced by draft embed cards in editor content. */
  referencedChildDraftIds: string[]
  /** Child draft ids whose cards were removed and should be deleted on publish/discard unless restored by undo. */
  pendingDeletedChildDraftIds: string[]
  /**
   * True when `publish.start` is received while an autosave/create is in flight
   * or while the draft has unsaved changes. Causes the machine to flush the
   * pending save and then enter `publishing` once `_save.completed` raises.
   * Cleared on entry to `publishing` so a second publish click does not double-fire.
   */
  pendingPublish: boolean
  pendingExitEditingAfterSave: boolean
  error: unknown
  /**
   * Non-fatal resource fetch state surfaced by the consumer (`useResource`). Drives a banner
   * in the page header without changing the machine's top-level state. `null` when the
   * resource fetch is in a healthy state.
   */
  transientResourceError: TransientResourceError
  /** True after this old-version visit has already warned about blocked edit attempts. */
  oldVersionEditNoticeShown: boolean
  /**
   * Durable path the user picked for a not-yet-published draft via the rename
   * affordance. Hydrated from the draft on load, persisted via `writeDraft`, and
   * honored at publish time in place of the title-derived slug.
   */
  publishPath: string[] | null
  /**
   * The working schema of a draft whose document defines a type (see `schema-draft.ts`). It lives
   * beside the metadata, not in it; hydrated from the draft, persisted via `writeDraft`, and frozen
   * into the document's `schemaDefinition` at publish.
   */
  schemaDraft: SchemaDraft
  /**
   * The working attributes schemas behind this document's `attributesSchema` and its children's
   * `childAttributesSchema`, by key (see `schema-draft.ts`). Same lifecycle as `schemaDraft`: beside
   * the metadata, persisted via `writeDraft`, frozen into IPFS objects at publish.
   */
  bindingSchemaDrafts: BindingSchemaDrafts
  /** Error from the most recent rename attempt, surfaced in the publish popover. */
  renameError: string | null
  /** Transient path captured from `rename.commit`/`rename.retry` while the rename actor runs. */
  renameTargetPath: string[] | null
}

function contentToEditorBlocks(content: HMBlockNode[] | EditorBlock[] | null | undefined): EditorBlock[] {
  if (!content?.length) return []
  const first = content[0]
  return first && 'block' in first
    ? hmBlocksToEditorContent(content as HMBlockNode[], {childrenType: 'Group'})
    : (content as EditorBlock[])
}

/** Returns the machine's authoritative editor content for collection rendering and repair. */
export function getCollectionEditorBlocks(context: DocumentMachineContext): EditorBlock[] {
  const useDraft = shouldAllowDraftOverlay(context.isLatestVersion, context.routeVersion) && context.draftContent
  return contentToEditorBlocks(useDraft || context.document?.content)
}

function getMachineOwnedContentOverride(context: DocumentMachineContext): EditorBlock[] | undefined {
  const publishedType = deriveDocumentType(context.document?.content, context.documentId)
  if (context.documentType !== 'collection' && publishedType !== 'collection') return undefined
  // A collection's body is its query block, owned by the machine. Before any query edit there is no
  // draft content yet, so fall back to the published query rather than letting the writer read an
  // (empty) editor, which would drop the query and make publish offer to delete every child.
  return contentToEditorBlocks(context.draftContent ?? context.document?.content)
}

/** Returns the effective metadata derived from the published document and draft overlay. */
export function getEffectiveDocumentMetadata(context: DocumentMachineContext): HMMetadata {
  return {...(context.document?.metadata ?? {}), ...context.metadata}
}

/**
 * Non-fatal resource fetch state derived by the consumer of {@link useResource}.
 * Stored in {@link DocumentMachineContext} so banners and debug tooling read from
 * a single source of truth.
 */
export type TransientResourceError =
  | {kind: 'refetch-error'; message: string}
  | {kind: 'discovering'}
  | {kind: 'not-found-transient'}
  | null

/** All events the machine can receive. */
export type DocumentMachineEvent =
  | {type: 'document.loaded'; document: HMDocument}
  | {type: 'document.error'; error: unknown}
  | {type: 'document.retry'}
  | {type: 'edit.start'; cursorPosition?: EditCursorPosition | null}
  | {type: 'edit.cancel'}
  | {
      type: 'change'
      metadata?: HMDraft['metadata']
      /** The working schema behind `schemaDefinition`; `null` drops it. */
      schemaDraft?: Record<string, any> | null
      /** Binding schemas to draft, by key; `null` drops a key's draft (cleared or repointed). */
      bindingSchemaDrafts?: Partial<Record<BindingSchemaKey, Record<string, any> | null>>
    }
  | {type: 'rootChildrenType.change'; childrenType: HMBlockChildrenType}
  | {type: 'change.navigation'; navigation: HMNavigationItem[]}
  | {type: 'reset.content'}
  | {type: 'collection.query.change'; props: Partial<EditorQueryBlock['props']>}
  | {type: 'collection.convertToCollection'}
  | {type: 'collection.convertToDocument'}
  | {
      type: 'publish.start'
      /**
       * Optional explicit path the user picked in the publish popover. Wins over
       * the inline first-publish slug rename in `usePublishResource`.
       */
      pathOverride?: string[]
    }
  | {type: 'publish.confirmChildDeletion'}
  | {type: 'publish.cancelChildDeletion'}
  | {type: 'document.remoteUpdate'; document: HMDocument}
  | {type: 'edit.discard'}
  | {type: 'childReferences.removed'; documentIds: string[]}
  | {type: 'childDraftRefs.changed'; draftIds: string[]}
  | {type: 'capability.changed'; canEdit: boolean}
  | {type: 'account.changed'; signingAccountId?: string; publishAccountUid?: string}
  | {type: 'version.changed'; isLatest: boolean; routeVersion?: string | null}
  | {type: 'draft.existing'; draftId: string}
  | {
      type: 'draft.resolved'
      draftId: string | null
      content: HMBlockNode[] | null
      cursorPosition: number | null
      metadata?: HMMetadata | null
      deps?: string[] | null
      maintenanceRevision?: number
      /** Block IDs the user previously touched in this draft (persisted across reloads). */
      removedChildDocumentIds?: string[] | null
      mineTouchedIds?: string[] | null
      /** Three-way merge base captured when the draft was first started or last rebased. */
      baseBlocks?: HMBlockNode[] | null
      /** Durable rename path persisted on the draft. */
      publishPath?: string[] | null
      /** The working schema persisted on the draft. */
      schemaDraft?: Record<string, any> | null
      /** The working binding schemas persisted on the draft. */
      bindingSchemaDrafts?: BindingSchemaDrafts
    }
  | {
      type: 'draft.externallyModified'
      cardBlockId?: string
      childDraftId?: string
      targetBlockId?: string
      jobId?: string
      operation?: 'add' | 'remove' | 'rewrite'
      sourceDocumentId?: string
      targetDocumentId?: string
      previousContent?: HMBlockNode[]
      publishedDocument?: HMDocument
      draftId: string
      source?: 'document-card-cleanup'
      deletedDocumentId?: string
      removedBlockIds?: string[]
      content?: HMBlockNode[] | null
      cursorPosition?: number | null
      metadata?: HMMetadata | null
      deps?: string[] | null
      maintenanceRevision?: number
      removedChildDocumentIds?: string[] | null
      mineTouchedIds?: string[] | null
      baseBlocks?: HMBlockNode[] | null
      publishPath?: string[] | null
    }
  | {type: '_save.started'}
  | {type: '_save.completed'}
  | {type: 'editor.baselineUpdate'; blocks: EditorBlock[]}
  | {type: 'scroll'}
  | {type: 'rebase.blockTouched'; blockIds: string[]}
  | {type: 'rebase.apply'; mergedBlocks: HMBlockNode[]; newDocument: HMDocument}
  | {type: 'rebase.detectConflict'; conflictedBlockIds: string[]; author: string | null}
  | {type: 'rebase.dismiss'}
  | {type: 'resource.transientError'; error: NonNullable<TransientResourceError>}
  | {type: 'resource.recovered'}
  | {type: 'rename.start'}
  | {type: 'rename.cancel'}
  | {type: 'rename.commit'; path: string[]}
  | {type: 'rename.retry'; path: string[]}

/** Input for the writeDraft actor. */
export type WriteDraftInput = {
  draftId: string | null
  metadata: HMDraft['metadata']
  deps: string[]
  maintenanceRevision?: number
  navigation: HMNavigationItem[] | undefined
  locationUid: string
  locationPath: string[]
  editUid: string
  editPath: string[]
  signingAccountId: string | null
  /** Explicit authored child-reference removals retained across reloads. */
  removedChildDocumentIds?: string[]
  /** Block IDs the user has locally touched since edit-start (or last rebase). */
  mineTouchedIds: string[]
  /** Three-way merge base captured at edit-start (or updated by `rebase.apply`). */
  baseBlocks: HMBlockNode[] | null
  /** Durable rename path persisted on the draft. */
  publishPath?: string[] | null
  /** The draft's working schema, when its document defines a type. */
  schemaDraft?: SchemaDraft
  /** The draft's working binding schemas, by key. */
  bindingSchemaDrafts?: BindingSchemaDrafts
  /** Explicit editor content for machine-owned repairs when no editor is mounted. */
  contentOverride?: EditorBlock[]
}

/** Output returned by draft writers after the saved draft hits local storage. */
export type WriteDraftOutput = {
  id: string
  content?: HMBlockNode[] | null
  cursorPosition?: number | null
}

/** Input for the publishDocument actor. */
export type PublishInput = {
  documentId: UnpackedHypermediaId
  draftId: string
  deps: string[]
  metadata: HMDraft['metadata']
  navigation: HMNavigationItem[] | undefined
  publishAccountUid: string | null
  /**
   * Optional explicit destination path the user picked in the publish popover.
   * When set, the consumer-provided actor must use it as-is and skip the
   * inline first-publish slug rename.
   */
  pathOverride?: string[]
  /** Exact child-subtree scope approved before publishing this parent. */
  confirmedChildDeletions?: ConfirmedChildDeletion[]
  /** Child drafts removed from this parent draft and confirmed by publishing. */
  deletedChildDraftIds: string[]
}

/** Input for the discardDraft actor. */
export type DiscardDraftInput = {
  draftId: string
  /** Child drafts removed from this parent draft and confirmed by discarding. */
  deletedChildDraftIds: string[]
}

/** Input for the pushDocument actor, fired after a successful publish. */
export type PushDocumentInput = {
  publishedDocument: HMDocument
}

/** Input for the renameDocument actor — relocates an already-published document. */
export type RenameDocumentInput = {
  from: UnpackedHypermediaId
  path: string[]
  signingAccountUid: string | null
}

/** Output from the renameDocument actor. */
export type RenameDocumentOutput = {
  to: UnpackedHypermediaId
}

/** Input for the renameDraft actor — persists the rename path onto an unpublished draft. */
export type RenameDraftInput = {
  draftId: string
  path: string[]
}

export type DocumentMachineState = StateFrom<typeof documentMachine>

/** Parallel rename region shared by the `loaded` and `editing` top-level states. */
function renameRegion(): any {
  return {
    initial: 'idle',
    states: {
      idle: {
        on: {
          'rename.start': {target: 'renaming'},
        },
      },
      renaming: {
        on: {
          'rename.cancel': {target: 'idle', actions: ['clearRenameTarget']},
          'rename.commit': [
            {target: 'committingPublished', guard: 'isPublished', actions: ['captureRenamePath']},
            {target: 'committingDraft', actions: ['captureRenamePath']},
          ],
        },
      },
      committingPublished: {
        entry: ['clearRenameError'],
        invoke: {
          id: 'renameDocument',
          src: 'renameDocument',
          input: ({context}: {context: DocumentMachineContext}) => ({
            from: context.documentId,
            path: context.renameTargetPath ?? [],
            signingAccountUid: context.signingAccountId ?? context.publishAccountUid,
          }),
          onDone: {
            target: 'idle',
            actions: ['emitRenamed', 'clearRenameTarget'],
          },
          onError: {
            target: 'error',
            actions: ['setRenameError'],
          },
        },
      },
      committingDraft: {
        entry: ['clearRenameError'],
        invoke: {
          id: 'renameDraft',
          src: 'renameDraft',
          input: ({context}: {context: DocumentMachineContext}) => ({
            draftId: context.draftId ?? '',
            path: context.renameTargetPath ?? [],
          }),
          onDone: {
            target: 'idle',
            actions: ['setPublishPathFromResult'],
          },
          onError: {
            target: 'error',
            actions: ['setRenameError'],
          },
        },
      },
      error: {
        on: {
          'rename.retry': [
            {target: 'committingPublished', guard: 'isPublished', actions: ['captureRenamePath']},
            {target: 'committingDraft', actions: ['captureRenamePath']},
          ],
          'rename.cancel': {target: 'idle', actions: ['clearRenameTarget']},
        },
      },
    },
  }
}

// -- Machine --

export const documentMachine = setup({
  types: {
    input: {} as DocumentMachineInput,
    context: {} as DocumentMachineContext,
    events: {} as DocumentMachineEvent,
    emitted: {} as
      | {type: 'scrolling'}
      | {type: 'oldVersionEditBlocked'}
      | {type: 'maintenanceConflict'; jobId: string; error: string}
      | {type: 'renamed'; oldId: string; newId: string},
  },
  actions: {
    setDocumentData: assign({
      document: ({event}) => {
        if (event.type === 'document.loaded' || event.type === 'document.remoteUpdate') {
          return event.document
        }
        return null
      },
      publishedVersion: ({event}) => {
        if (event.type === 'document.loaded' || event.type === 'document.remoteUpdate') {
          return event.document.version
        }
        return null
      },
      editorBaseline: ({event}) => {
        if (event.type === 'document.loaded' || event.type === 'document.remoteUpdate') {
          return hmBlocksToEditorContent(event.document.content ?? [], {childrenType: 'Group'})
        }
        return null
      },
      documentType: ({context, event}) => {
        if (event.type !== 'document.loaded' && event.type !== 'document.remoteUpdate') return context.documentType
        const content =
          shouldAllowDraftOverlay(context.isLatestVersion, context.routeVersion) && context.draftContent
            ? context.draftContent
            : event.document.content
        return deriveDocumentType(content, context.documentId)
      },
    }),
    setTransientResourceError: assign({
      transientResourceError: ({context, event}) => {
        if (event.type !== 'resource.transientError') return context.transientResourceError
        return event.error
      },
    }),
    clearTransientResourceError: assign({
      transientResourceError: null,
    }),
    markOldVersionEditNoticeShown: assign({
      oldVersionEditNoticeShown: true,
    }),
    setMetadata: assign({
      metadata: ({context, event}) => {
        if (event.type === 'change' && event.metadata) {
          return {...context.metadata, ...event.metadata}
        }
        if (event.type === 'rootChildrenType.change') {
          return {...context.metadata, childrenType: event.childrenType}
        }
        return context.metadata
      },
      // A `change` may also carry the working schema, which is draft state beside the metadata.
      schemaDraft: ({context, event}) =>
        event.type === 'change' && event.schemaDraft !== undefined ? event.schemaDraft : context.schemaDraft,
      // A `change` may carry one or more binding schemas; they merge over the ones already drafted,
      // and a `null` drops that key's draft (cleared, or repointed at a reference).
      bindingSchemaDrafts: ({context, event}) => {
        if (event.type !== 'change' || !event.bindingSchemaDrafts) return context.bindingSchemaDrafts
        const next: NonNullable<BindingSchemaDrafts> = {...(context.bindingSchemaDrafts ?? {})}
        for (const [key, schema] of Object.entries(event.bindingSchemaDrafts) as Array<
          [BindingSchemaKey, Record<string, any> | null | undefined]
        >) {
          if (schema) next[key] = schema
          else delete next[key]
        }
        return Object.keys(next).length ? next : null
      },
    }),
    updateCollectionQuery: assign({
      draftContent: ({context, event}) => {
        if (event.type !== 'collection.query.change') return context.draftContent
        const blocks = getCollectionEditorBlocks(context)
        const queryIndex = blocks.findIndex((block) => block.type === 'query')
        const query = blocks[queryIndex]
        if (!query || query.type !== 'query') return context.draftContent
        const next = [...blocks]
        const nextQuery = {...query, props: {...query.props, ...event.props}}
        next[queryIndex] = nextQuery
        return next as unknown as HMBlockNode[]
      },
      documentType: 'collection',
    }),
    convertToCollection: assign({
      draftContent: () => [createDefaultCollectionQueryBlock(nanoid(8))] as unknown as HMBlockNode[],
      documentType: 'collection',
    }),
    convertToDocument: assign({
      draftContent: () => {
        return []
      },
      documentType: 'document',
    }),
    setNavigation: assign({
      navigation: ({event}) => {
        if (event.type === 'change.navigation') {
          return event.navigation
        }
        return undefined
      },
    }),
    setDepsFromPublished: assign({
      deps: ({context}) => {
        if (context.draftId && context.deps.length) return context.deps
        return context.publishedVersion ? [context.publishedVersion] : context.deps
      },
    }),
    setDraftIdFromResult: assign({
      draftId: (_, params: {id: string}) => params.id,
    }),
    setDraftSavedSnapshotFromResult: assign({
      draftContent: ({context}, params: WriteDraftOutput) => {
        if (params.content !== undefined) return params.content ?? null
        return context.draftContent
      },
      draftCursorPosition: ({context}, params: WriteDraftOutput) => {
        if (params.cursorPosition !== undefined) return params.cursorPosition ?? null
        return context.draftCursorPosition
      },
    }),
    setDraftCreated: assign({
      draftCreated: true,
    }),
    resetChangeWhileSaving: assign({
      hasChangedWhileSaving: false,
    }),
    setHasChangedWhileSaving: assign({
      hasChangedWhileSaving: true,
    }),
    setError: assign({
      error: ({event}) => {
        if (event.type === 'document.error') {
          return event.error
        }
        return null
      },
    }),
    clearShouldAutoEdit: assign({
      shouldAutoEdit: false,
    }),
    setPendingEditCursorPosition: assign({
      pendingEditCursorPosition: ({event}) => {
        if (event.type !== 'edit.start') return null
        return event.cursorPosition ?? null
      },
    }),
    setCanEdit: assign({
      canEdit: ({event}) => {
        if (event.type === 'capability.changed') {
          return event.canEdit
        }
        return false
      },
    }),
    setRouteVersionState: assign({
      isLatestVersion: ({event}) => (event.type === 'version.changed' ? event.isLatest : true),
      routeVersion: ({context, event}) => {
        if (event.type !== 'version.changed') return context.routeVersion
        return event.routeVersion !== undefined ? event.routeVersion : context.routeVersion
      },
      oldVersionEditNoticeShown: ({context, event}) => {
        if (event.type !== 'version.changed') return context.oldVersionEditNoticeShown
        const routeVersion = event.routeVersion !== undefined ? event.routeVersion : context.routeVersion
        return shouldAllowDraftOverlay(event.isLatest, routeVersion) ? false : context.oldVersionEditNoticeShown
      },
    }),
    clearDraftState: assign({
      documentType: ({context}) => deriveDocumentType(context.document?.content ?? [], context.documentId),
      draftId: null,
      draftCreated: false,
      hasChangedWhileSaving: false,
      draftContent: null,
      draftCursorPosition: null,
      pendingEditCursorPosition: null,
      metadata: {},
      navigation: undefined,
      baseBlocks: null,
      removedChildDocumentIds: [],
      mineTouchedIds: [],
      maintenanceRevision: 0,
      pendingRemoteDocument: null,
      pendingRebase: null,
      pendingPathOverride: null,
      pendingPublish: false,
      pendingExitEditingAfterSave: false,
      referencedChildDraftIds: [],
      pendingDeletedChildDraftIds: [],
      publishPath: null,
      schemaDraft: null,
      bindingSchemaDrafts: null,
      renameError: null,
      renameTargetPath: null,
    }),
    clearEditingState: assign({
      // Preserve draftId and metadata so re-entering editing reuses the same draft
      // and title/summary changes remain visible outside editing mode.
      hasChangedWhileSaving: false,
      pendingEditCursorPosition: null,
      baseBlocks: null,
      removedChildDocumentIds: [],
      mineTouchedIds: [],
      pendingRemoteDocument: null,
      pendingRebase: null,
      pendingPathOverride: null,
      pendingPublish: false,
      pendingExitEditingAfterSave: false,
      renameError: null,
      renameTargetPath: null,
    }),
    promotePendingRemoteDocument: assign({
      document: ({context}) =>
        context.pendingRemoteDocument?.version === context.routeVersion
          ? context.pendingRemoteDocument
          : context.document,
      publishedVersion: ({context}) =>
        context.pendingRemoteDocument?.version === context.routeVersion
          ? context.pendingRemoteDocument.version
          : context.publishedVersion,
      editorBaseline: ({context}) => {
        if (context.pendingRemoteDocument?.version !== context.routeVersion) return context.editorBaseline
        return hmBlocksToEditorContent(context.pendingRemoteDocument.content ?? [], {childrenType: 'Group'})
      },
    }),
    snapshotBaseBlocks: assign({
      baseBlocks: ({context}) => {
        // Preserve restored base blocks from a reloaded draft. We only snapshot
        // fresh from the published document when starting a brand-new draft
        // session (baseBlocks === null and not yet hydrated by draft.resolved).
        if (context.baseBlocks !== null) {
          // console.log('[Rebase machine] snapshotBaseBlocks: preserving restored', {
          //   count: context.baseBlocks.length,
          // })
          return context.baseBlocks
        }
        const blocks = context.document?.content ?? []
        // console.log('[Rebase machine] snapshotBaseBlocks: fresh', {
        //   count: blocks.length,
        //   ids: blocks.map((n) => n.block?.id),
        //   publishedVersion: context.publishedVersion,
        // })
        return blocks
      },
      mineTouchedIds: ({context}) => {
        // Preserve restored mineTouchedIds across reload; reset only on a truly
        // fresh edit-start where nothing was loaded from disk.
        if (context.mineTouchedIds.length) return context.mineTouchedIds
        return []
      },
    }),
    appendMineTouched: assign({
      mineTouchedIds: ({context, event}) => {
        if (event.type !== 'rebase.blockTouched') return context.mineTouchedIds
        if (!event.blockIds.length) return context.mineTouchedIds
        const seen = new Set(context.mineTouchedIds)
        let changed = false
        for (const id of event.blockIds) {
          if (!seen.has(id)) {
            seen.add(id)
            changed = true
          }
        }
        const next = changed ? Array.from(seen) : context.mineTouchedIds
        if (changed) {
          // console.log('[Rebase machine] appendMineTouched', {added: event.blockIds, total: next})
        }
        return next
      },
    }),
    setPendingRemoteDocument: assign({
      pendingRemoteDocument: ({event}) => {
        if (event.type === 'document.remoteUpdate') {
          // console.log('[Rebase machine] setPendingRemoteDocument', {
          //   version: event.document.version,
          //   blockIds: (event.document.content ?? []).map((n) => n.block?.id),
          // })
          return event.document
        }
        return null
      },
    }),
    applyRebaseMerge: assign({
      document: ({context, event}) => {
        if (event.type !== 'rebase.apply') return context.document
        // console.log('[Rebase machine] applyRebaseMerge', {
        //   fromDeps: context.deps,
        //   toDeps: event.newDocument.version ? [event.newDocument.version] : context.deps,
        //   fromMineTouched: context.mineTouchedIds,
        //   mergedBlockCount: event.mergedBlocks.length,
        // })
        return event.newDocument
      },
      publishedVersion: ({context, event}) => {
        if (event.type !== 'rebase.apply') return context.publishedVersion
        return event.newDocument.version
      },
      deps: ({context, event}) => {
        if (event.type !== 'rebase.apply') return context.deps
        return event.newDocument.version ? event.newDocument.version.split('.') : context.deps
      },
      baseBlocks: ({context, event}) => {
        if (event.type !== 'rebase.apply') return context.baseBlocks
        return event.newDocument.content
      },
      mineTouchedIds: ({context, event}) => {
        if (event.type !== 'rebase.apply') return context.mineTouchedIds
        const published = createBlocksMap(event.newDocument.content, '')
        const merged = createBlocksMap(event.mergedBlocks, '')
        const previouslyTouched = new Set(context.mineTouchedIds)
        return Array.from(new Set([...Object.keys(published), ...Object.keys(merged)])).filter((id) => {
          const before = published[id]
          const after = merged[id]
          if (!before || !after || !isBlocksEqual(before.block, after.block)) return true
          return previouslyTouched.has(id) && (before.parent !== after.parent || before.left !== after.left)
        })
      },
      pendingRemoteDocument: null,
      pendingRebase: null,
    }),
    setRebaseConflict: assign({
      pendingRebase: ({event}): PendingRebase | null => {
        if (event.type !== 'rebase.detectConflict') return null
        return {
          kind: 'conflict',
          conflictedBlockIds: event.conflictedBlockIds,
          author: event.author,
        }
      },
    }),
    clearPendingRebase: assign({
      pendingRebase: null,
    }),
    clearPendingRemoteUpdate: assign({
      pendingRemoteDocument: null,
      pendingRebase: null,
    }),
    setPathOverrideFromEvent: assign({
      pendingPathOverride: ({context, event}) => {
        if (event.type !== 'publish.start') return context.pendingPathOverride
        // Preserve a previously captured override when a queued publish click
        // arrives without one — first click wins for the path argument.
        return event.pathOverride ?? context.pendingPathOverride
      },
    }),
    clearPathOverride: assign({
      pendingPathOverride: null,
    }),
    captureRenamePath: assign({
      renameTargetPath: ({context, event}) =>
        event.type === 'rename.commit' || event.type === 'rename.retry' ? event.path : context.renameTargetPath,
      renameError: null,
    }),
    clearRenameError: assign({
      renameError: null,
    }),
    setRenameError: assign({
      renameError: ({event}) => {
        const err = (event as any).error
        return err instanceof Error ? err.message : typeof err === 'string' ? err : 'Rename failed'
      },
    }),
    setPublishPathFromResult: assign({
      publishPath: ({context}) => context.renameTargetPath,
      renameTargetPath: null,
    }),
    clearRenameTarget: assign({
      renameTargetPath: null,
    }),
    emitRenamed: emit(({context, event}): {type: 'renamed'; oldId: string; newId: string} => {
      const output = (event as any).output as RenameDocumentOutput | undefined
      return {
        type: 'renamed',
        oldId: context.documentId.id,
        newId: output?.to?.id ?? context.documentId.id,
      }
    }),
    markPendingPublish: assign({
      pendingPublish: true,
    }),
    clearPendingPublish: assign({
      pendingPublish: false,
    }),
    markPendingExitEditingAfterSave: assign({
      pendingExitEditingAfterSave: true,
    }),
    clearPendingExitEditingAfterSave: assign({
      pendingExitEditingAfterSave: false,
    }),
    updateChildDraftRefs: assign({
      referencedChildDraftIds: ({event, context}) => {
        if (event.type !== 'childDraftRefs.changed') return context.referencedChildDraftIds
        return Array.from(new Set(event.draftIds.filter((id) => !!id)))
      },
      pendingDeletedChildDraftIds: ({event, context}) => {
        if (event.type !== 'childDraftRefs.changed') return context.pendingDeletedChildDraftIds
        const nextRefs = new Set(event.draftIds.filter((id) => !!id))
        const prevRefs = new Set(context.referencedChildDraftIds)
        const pending = new Set(context.pendingDeletedChildDraftIds)
        for (const id of Array.from(prevRefs)) {
          if (!nextRefs.has(id)) pending.add(id)
        }
        for (const id of Array.from(nextRefs)) {
          pending.delete(id)
        }
        return Array.from(pending)
      },
    }),
    markDocumentReady: assign({
      documentReady: true,
    }),
    setDraftResolved: assign({
      draftReady: true,
      draftId: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.draftId) return event.draftId
        return context.draftId
      },
      draftCreated: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.draftId) return true
        return context.draftCreated
      },
      shouldAutoEdit: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.draftId) return true
        return context.shouldAutoEdit
      },
      draftContent: ({event}) => {
        if (event.type === 'draft.resolved') return event.content ?? null
        return null
      },
      documentType: ({context, event}) => {
        if (event.type !== 'draft.resolved') return context.documentType
        const content =
          shouldAllowDraftOverlay(context.isLatestVersion, context.routeVersion) && event.content
            ? event.content
            : context.document?.content
        return deriveDocumentType(content, context.documentId)
      },
      draftCursorPosition: ({event}) => {
        if (event.type === 'draft.resolved') return event.cursorPosition ?? null
        return null
      },
      metadata: ({event, context}) => {
        // An older draft kept its working schema in metadata; it is lifted out (see schemaDraft).
        if (event.type === 'draft.resolved' && event.metadata) return splitLegacySchemaDraft(event.metadata).metadata
        return context.metadata
      },
      schemaDraft: ({event, context}) => {
        if (event.type !== 'draft.resolved') return context.schemaDraft
        return draftSchemaDraft(event) ?? context.schemaDraft
      },
      bindingSchemaDrafts: ({event, context}) => {
        if (event.type !== 'draft.resolved') return context.bindingSchemaDrafts
        return draftBindingSchemaDrafts(event) ?? context.bindingSchemaDrafts
      },
      deps: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.deps && event.deps.length) return event.deps
        return context.deps
      },
      removedChildDocumentIds: ({event, context}) =>
        event.type === 'draft.resolved'
          ? event.removedChildDocumentIds ?? context.removedChildDocumentIds
          : context.removedChildDocumentIds,
      maintenanceRevision: ({event, context}) =>
        event.type === 'draft.resolved' ? event.maintenanceRevision ?? 0 : context.maintenanceRevision,
      mineTouchedIds: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.mineTouchedIds && event.mineTouchedIds.length) {
          return event.mineTouchedIds
        }
        return context.mineTouchedIds
      },
      baseBlocks: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.baseBlocks) {
          return event.baseBlocks
        }
        return context.baseBlocks
      },
      publishPath: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.publishPath && event.publishPath.length) {
          return event.publishPath
        }
        return context.publishPath
      },
      referencedChildDraftIds: ({event, context}) => {
        if (event.type === 'draft.resolved' && event.content) return collectChildDraftIds(event.content)
        return context.referencedChildDraftIds
      },
    }),
    setExistingDraft: assign({
      draftId: ({event}) => {
        if (event.type === 'draft.existing') {
          return event.draftId
        }
        return null
      },
      draftCreated: true,
      shouldAutoEdit: true,
      draftReady: true,
    }),
    setAccountIds: assign({
      signingAccountId: ({context, event}) => {
        if (event.type === 'account.changed' && event.signingAccountId !== undefined) {
          return event.signingAccountId || null
        }
        return context.signingAccountId
      },
      publishAccountUid: ({context, event}) => {
        if (event.type === 'account.changed' && event.publishAccountUid !== undefined) {
          return event.publishAccountUid || null
        }
        return context.publishAccountUid
      },
    }),
    setEditorEditable: () => {
      // Provided via .provide() in the React layer (editor handlers ref)
    },
    setEditorReadOnly: () => {
      // Provided via .provide() in the React layer (editor handlers ref)
    },
    applyInitialContentToEditor: () => {
      // Provided via .provide() in the React layer (editor handlers ref)
    },
    notifyPublishError: (_, _params: {error: unknown}) => {
      // Provided by the host so publication failures are visible in the UI.
    },
    placeCursorFromPendingOrDraft: () => {
      // Provided via .provide() in the React layer (editor handlers ref)
    },
    pushContentToEditor: () => {
      // Provided via .provide() in the React layer (editor handlers ref)
    },
    pushPublishedContentToEditor: () => {
      // Provided via .provide() in the React layer (editor handlers ref)
    },
    updatePublishedVersion: assign({
      documentType: ({context, event}) => {
        const doc = (event as any).output as HMDocument
        return deriveDocumentType(doc?.content ?? [], context.documentId)
      },
      publishedVersion: ({event}) => {
        // After publish completes, the done event output is the new HMDocument
        // We extract the version from it
        const doc = (event as any).output as HMDocument
        return doc?.version ?? null
      },
      document: ({event}) => {
        const doc = (event as any).output as HMDocument
        return doc ?? null
      },
      editorBaseline: ({event}) => {
        const doc = (event as any).output as HMDocument
        if (!doc) return null
        return hmBlocksToEditorContent(doc.content ?? [], {childrenType: 'Group'})
      },
    }),
    setEditorBaselineFromSnapshot: assign({
      editorBaseline: ({event, context}) => {
        if (event.type === 'editor.baselineUpdate') return event.blocks
        return context.editorBaseline
      },
    }),
    applyExternalDraftState: assign(({context, event}) =>
      event.type === 'draft.externallyModified' ? reconcileLiveDocumentMaintenance(context, event, null) : {},
    ),
    logDraftResolved: ({context, event}) => {
      if (event.type !== 'draft.resolved') return
      documentEmbedCleanupInfo(`${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine draft.resolved`, {
        documentId: context.documentId.id,
        eventDraftId: event.draftId,
        currentDraftId: context.draftId,
        contentTopLevelBlockCount: getTopLevelBlockCount(event.content),
        hasMetadata: !!event.metadata,
        deps: event.deps ?? null,
        mineTouchedIds: event.mineTouchedIds ?? null,
        baseBlockCount: getTopLevelBlockCount(event.baseBlocks),
      })
    },
    logEnterEditing: ({context}) => {
      documentEmbedCleanupInfo(`${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine entering editing`, {
        documentId: context.documentId.id,
        draftId: context.draftId,
        draftCreated: context.draftCreated,
        shouldAutoEdit: context.shouldAutoEdit,
        draftContentTopLevelBlockCount: getTopLevelBlockCount(context.draftContent),
        documentTopLevelBlockCount: getTopLevelBlockCount(context.document?.content),
        editorBaselineTopLevelBlockCount: getTopLevelBlockCount(context.editorBaseline),
        contentSource: context.draftContent ? 'draft' : 'published-document',
      })
    },
    logDraftExternallyModified: ({context, event}) => {
      if (event.type !== 'draft.externallyModified') return
      documentEmbedCleanupInfo(`${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine draft externally modified`, {
        documentId: context.documentId.id,
        eventDraftId: event.draftId,
        currentDraftId: context.draftId,
        matchesCurrentDraft: !!context.draftId && context.draftId === event.draftId,
        source: event.source ?? null,
        deletedDocumentId: event.deletedDocumentId ?? null,
        removedBlockIds: event.removedBlockIds ?? null,
        incomingContentTopLevelBlockCount: getTopLevelBlockCount(event.content),
        stateStillUsesDraftContentTopLevelBlockCount: getTopLevelBlockCount(context.draftContent),
        documentTopLevelBlockCount: getTopLevelBlockCount(context.document?.content),
        hasPendingLocalSave: context.hasChangedWhileSaving,
        draftCreated: context.draftCreated,
      })
    },
    applyExternalDraftCleanupToEditor: () => {
      // Provided via .provide() in the React layer (editor handlers ref)
    },
    logSaveStarted: ({context}) => {
      documentEmbedCleanupInfo(`${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine draft save started`, {
        documentId: context.documentId.id,
        draftId: context.draftId,
        draftCreated: context.draftCreated,
        hasChangedWhileSaving: context.hasChangedWhileSaving,
        removedChildDocumentIds: context.removedChildDocumentIds,
        mineTouchedIds: context.mineTouchedIds,
        baseBlockCount: getTopLevelBlockCount(context.baseBlocks),
      })
    },
    logSaveCompleted: ({context}) => {
      documentEmbedCleanupInfo(`${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine draft save completed`, {
        documentId: context.documentId.id,
        draftId: context.draftId,
        draftCreated: context.draftCreated,
        hasChangedWhileSaving: context.hasChangedWhileSaving,
      })
    },
    logRemoteUpdateWhileEditing: ({context, event}) => {
      if (event.type !== 'document.remoteUpdate') return
      documentEmbedCleanupInfo(`${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine remote update while editing`, {
        documentId: context.documentId.id,
        draftId: context.draftId,
        incomingVersion: event.document.version,
        currentPublishedVersion: context.publishedVersion,
        incomingTopLevelBlockCount: getTopLevelBlockCount(event.document.content),
        draftContentTopLevelBlockCount: getTopLevelBlockCount(context.draftContent),
      })
    },
  },
  guards: {
    canTransitionToEditing: ({context}) => {
      const result =
        context.canEdit &&
        shouldAllowDraftOverlay(context.isLatestVersion, context.routeVersion) &&
        (context.isLatestVersion || !!context.draftId)
      // console.log('[DocMachine] guard canTransitionToEditing', {
      //   canEdit: context.canEdit,
      //   isLatestVersion: context.isLatestVersion,
      //   draftId: context.draftId,
      //   result,
      // })
      return result
    },
    shouldNotifyOldVersionEditBlocked: ({context}) =>
      context.canEdit &&
      !context.oldVersionEditNoticeShown &&
      !shouldAllowDraftOverlay(context.isLatestVersion, context.routeVersion),
    canOpenExistingDraft: ({context, event}) => {
      return (
        context.canEdit &&
        shouldAllowDraftOverlay(context.isLatestVersion, context.routeVersion) &&
        (context.isLatestVersion || (event.type === 'draft.existing' && !!event.draftId))
      )
    },
    didChangeWhileSaving: ({context}) => context.hasChangedWhileSaving,
    hasDraftId: ({context}) => context.draftId !== null,
    hasPersistedDraft: ({context}) => context.draftId !== null && context.draftCreated,
    hasPersistedDraftAndNoRebaseConflict: ({context}) =>
      context.draftId !== null && context.draftCreated && context.pendingRebase?.kind !== 'conflict',
    hasExistingDraft: ({context}) => context.shouldAutoEdit,
    hasRemoteUpdate: ({context}) => context.pendingRemoteDocument !== null,
    bothSourcesReady: ({context}) => context.documentReady && context.draftReady,
    capabilityLost: ({event}) => event.type === 'capability.changed' && !event.canEdit,
    hasPendingPublish: ({context}) => context.pendingPublish,
    hasPendingExitEditingAfterSave: ({context}) => context.pendingExitEditingAfterSave,
    noRebaseConflict: ({context}) => context.pendingRebase?.kind !== 'conflict',
    routeDisallowsDraftOverlay: ({context, event}) =>
      event.type === 'version.changed' &&
      !shouldAllowDraftOverlay(event.isLatest, event.routeVersion ?? context.routeVersion),
    isPublished: ({context}) => !!context.publishedVersion,
  },
  actors: {
    writeDraft: fromPromise<WriteDraftOutput, WriteDraftInput>(async () => {
      throw new Error('writeDraft actor must be provided via .provide()')
    }),
    inspectChildDeletions: fromPromise<ConfirmedChildDeletion[], ConfirmChildDeletionInput>(async () => {
      throw new Error('Child deletion inspection is unavailable; publication was not performed')
    }),
    publishDocument: fromPromise<HMDocument, PublishInput>(async () => {
      throw new Error('publishDocument actor must be provided via .provide()')
    }),
    discardDraft: fromPromise<void, DiscardDraftInput>(async () => {
      throw new Error('discardDraft actor must be provided via .provide()')
    }),
    renameDocument: fromPromise<RenameDocumentOutput, RenameDocumentInput>(async () => {
      throw new Error('renameDocument actor must be provided via .provide()')
    }),
    renameDraft: fromPromise<{path: string[]}, RenameDraftInput>(async () => {
      throw new Error('renameDraft actor must be provided via .provide()')
    }),
    pushDocument: fromPromise<void, PushDocumentInput>(async () => {
      // Default no-op: consumers that want push-on-publish must provide this actor.
      return
    }),
  },
  delays: {
    autosaveTimeout: 500,
    loadingTimeout: 10_000,
    saveIndicatorDismiss: 1000,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QBED2BjArgWzAOwBcAZASwDMx0BPdAGzADpbUBDCEvKAYggx3wJNWESAG0ADAF1EoAA6pYJAiVR4ZIAB6IAHOIDsDACyGAnCYCse7SYCM2mw4A0IKogBM58QwBsAZmt63tp6Nt4WFgC+Ec5oWLiEpBTUdIzMbBzcvHECDGAATnmoeRLSSCDyisqq6loIpiYMFuL22m7ihuIehs6uCG6+5gzawd5u-d7ifg5RMXzxxOSUNPRC6ZxcGrAELASMLGS7eQAUaeycACokuKiYBACUXLH8CYvJK6cZJeoVSipqZbVzDZBn5tL5jOITNpRsCeohzGEfOZhiY3CZDHpkcYZiAnvNEksUqsRBAuJAlAwtiw8gQvmUflV-qBajZfFCGOIbP1fA4uX5vOY4Qg9B4GG4HOJzFCJYYxji8QICW9UsJIDw5jk8mBsKhdgBVWQQHZgOlyBS-aoAxAmPQNKGmXydSFBcFChzaJHWNnacyGcyWQLyjUvJLLFVsNWm8rmxk1RCs9mc7m8tz8wUuRC+PxGaxhTwmbyGByGIPZEOElbk5TrKsMdAsPDoMC0KMMv5xhCszkMLlF8H2foWboZhD2HvBNzQqysnmF0vPBaholVjJcdAACwbMAYeBYADcSFAdn9WzH21bO3pwT5UZy9HpDKNbUKBjYOeY2j6gh-J9p5-jXjDXJ2GrbgNy3RhdwPI9GVEGxSjNSpz2ZeMr0MG82hse9HzcZ8RxTBhbUCItDAGP0oX-RVAOXEDVyyBcGC1HV9UNY1TyQy0UL6B8DFIvlmnFXQ3DdaEGA-CZjARcxfF8NxSMo8tlWA35OAYEgIHoNdN04E0pG+M9OM0dxWi8TpRk5STPEmIVMUGWxtCLe8HPsLCFMXCtGBXVT1M0rVYDAQR0FUXZCHYi0mSMvoTI5VNMMs8RrJHH03w8EJbKCAdzDcpUgK8qA1I0sAuFkTAACNaBIWB10pbYaTC2MLw-N0eV8G92mMMwEtlGxsuoytaNU8CdNJIaYHq5DIp5XCGF8B80UhLCpVTIVvyGIFrBsSErALHrolxYN3KUvK620mBST8gK62CgRxsM2opta3R7VTUj7JWgYhhMfR-A-DETFk3ql36lT8tGtVNm2XYGH2Q4jhYW4FH3MBLmuW4HgVRTcoG0HTrEPT6QMiL7twhoxgFcRZtTPRORs4IjARbwsL9YFbRMQGPOU0CTog0kIeNaGDnyOGEdgJGUbAG57keA6cpokHueGuCEOjDiieMtoYvMmx4sS3pqY9CwHx9UiMoLdmjuxustWPdZeDwRgOD3VAAGtGAAdzyJQwGQPIYdutW+lfIwC1MJ9ZWGXw3WBdDvELRnsNsaSSz2jHDqx+X0Gt0D1XttS8Cd12GA9r2fb9+D9NVjt+iBYPCwLXDw7BN0KZBfxbUlATCz0c3065zOwBt7h8kKPIGFkWgdjIIpsCLz3dlLg5-aroPQ9Dhvf0jkd0Vax1OllRmWqCHu5b7rPVzBpeLzabxeLCVMbAsLM+yFMw3xCWwQ+CdpwWP4HT4H7OF1ArXVCvjRC4Uq6TFvqiRmj9CysiFLmGauhSJf0dPvX+nlLaiwPLbVQDt84u3dnPb2vtF5gJVhAi8Ax0SETktJEYaI8J60mI0SUuFZrWHRLtWYZY04nwyJSfcdF8F5wLsQkuZDaTlwJpXahjpBgP0COKKYQQX5XjFJw2BN8hxZRTjLPqWD5Y4NXMPIoY8J4ECnnkGexd55SMvlxcEGsBiTFTJKHknQhSmHQmMbWhYAxjFRJgzmgiTHrAvhQtsd1MwClaglL6WYrybShN4IUWIhi2jkgMIEkpQghOOuE7gQCrqEBulEwmHYszSQ5JCCmN9PGpO8dYMSHRDA+kCNTTw94QklXKpVdcgiOAAAVChQD8rAHOBDxFjzKhVKqqdHGTWaIRAstopTEVZHoRB3Yiwuk5AKOw4pelzIGUMvAozUDjLgJMsxo9x6T2nrM-pCyDpLNqDaVqsUzDBHaeKCwiDURiTCK+bqzQH4nJeYMwa9AGwZANFwd51oNHfKhEbf56ZejSTcKsiYaJ0GShvlEPaeBUAiHgGUVOst6AVyoVxAAtP4sS99QiDmkveIU9K7JmB5QKG+j5qZuBCR8TgtKGpcSBdCQs-gKaoP+m6IsYkZKJMhOJMwejeELmpeGEkYqJosjJjNGSD8ZyTD9MOXoAwDaYk6FCGSyIgQav2nw7VoTRWyLpZFUIok753gfE+EwQpUyPTjknHJvp5L6JdYYt1+UfJgD1TE7iDgjBFm1jOEIyI0lb0LHQx0LQsKEr-FGrVMbjpgwgImgOPJZRrU5Ale8skPyYsQFYbwPYvqFmGC0AsbMS0ASBkY-+g8q2QMfEMLCN8b5SlGNJKOdhCJ+jJiKKUsli2aoHRzQpwj3XgPFZNPwOKBgPkdbNZom89bIiGLHa+WzoQA37VRQdzz5nQqgKOi89L2TiQcKMNk7KQgrWzH4La1cEmBkfZjIkfTX3nMudc2AFK936sQFyrwP7WX-tmiEC1rbbC1K7t67ptpIWwZhQPPA8LZAfq4s2nsmJbAU02oEawNlPATvsP9aEzQP4FIKEUGjkURQpQ6X4fwX0sLbJHMYdtbJJwZTcTabwxKIhAA */
  id: 'DocumentLifecycle',
  on: {
    scroll: {
      actions: emit({type: 'scrolling'}),
    },
    'editor.baselineUpdate': {
      actions: ['setEditorBaselineFromSnapshot'],
    },
    'childReferences.removed': {
      actions: assign({
        removedChildDocumentIds: ({context, event}) =>
          Array.from(new Set([...context.removedChildDocumentIds, ...event.documentIds])),
      }),
    },
    'childDraftRefs.changed': {
      actions: ['updateChildDraftRefs'],
    },
    'resource.transientError': {
      actions: ['setTransientResourceError'],
    },
    'resource.recovered': {
      actions: ['clearTransientResourceError'],
    },
    'draft.externallyModified': {
      actions: ['logDraftExternallyModified', 'applyExternalDraftState', 'applyExternalDraftCleanupToEditor'],
    },
  },
  context: ({input}) => ({
    documentId: input.documentId,
    documentType: 'document',
    draftId: input.existingDraftId ?? input.reservedDraftId ?? null,
    document: null,
    metadata: {},
    deps: input.deps ?? [],
    maintenanceRevision: input.maintenanceRevision ?? 0,
    confirmedChildDeletions: [],
    removedChildDocumentIds: [],
    childDeletionError: null,
    publishedVersion: null,
    isLatestVersion: input.isLatest ?? true,
    routeVersion: input.routeVersion ?? input.documentId.version ?? null,
    navigation: undefined,
    locationUid: input.locationUid ?? '',
    locationPath: input.locationPath ?? [],
    editUid: input.editUid ?? '',
    editPath: input.editPath ?? [],
    canEdit: input.canEdit,
    hasChangedWhileSaving: false,
    draftCreated: !!input.existingDraftId,
    shouldAutoEdit: !!input.existingDraftId || !!input.reservedDraftId,
    signingAccountId: input.signingAccountId ?? null,
    publishAccountUid: input.publishAccountUid ?? null,
    documentReady: false,
    draftReady: !!input.existingDraftId || !!input.reservedDraftId,
    draftContent: null,
    draftCursorPosition: null,
    pendingEditCursorPosition: null,
    editorBaseline: null,
    baseBlocks: null,
    mineTouchedIds: [],
    pendingRemoteDocument: null,
    pendingRebase: null,
    pendingPathOverride: null,
    pendingPublish: false,
    pendingExitEditingAfterSave: false,
    referencedChildDraftIds: [],
    pendingDeletedChildDraftIds: [],
    error: null,
    transientResourceError: null,
    oldVersionEditNoticeShown: false,
    publishPath: null,
    schemaDraft: null,
    bindingSchemaDrafts: null,
    renameError: null,
    renameTargetPath: null,
  }),
  initial: 'loading',
  states: {
    loading: {
      on: {
        'document.remoteUpdate': {
          // A refetch can finish before the draft query. Keep the newest parent
          // instead of dropping it after useDocumentSync has recorded its version.
          actions: ['setDocumentData', 'markDocumentReady'],
        },
        'document.loaded': {
          // Don't transition yet — wait for draft resolution too.
          actions: ['setDocumentData', 'markDocumentReady'],
        },
        'draft.resolved': {
          actions: ['logDraftResolved', 'setDraftResolved'],
        },
        'document.error': {
          // Stay in loading — React Query retries in the background.
          // Store the error for optional inline display (e.g. "retrying…").
          actions: ['setError'],
        },
        'capability.changed': {
          actions: ['setCanEdit'],
        },
        'account.changed': {
          actions: ['setAccountIds'],
        },
        'draft.existing': {
          actions: ['setExistingDraft'],
        },
        'version.changed': {
          actions: ['setRouteVersionState'],
        },
      },
      always: [{target: 'loaded', guard: 'bothSourcesReady'}],
      after: {
        loadingTimeout: {
          target: 'error',
        },
      },
    },

    loaded: {
      type: 'parallel',
      on: {
        'edit.start': [
          {
            target: 'editing',
            guard: 'canTransitionToEditing',
            actions: ['setPendingEditCursorPosition', 'setDepsFromPublished', 'snapshotBaseBlocks'],
          },
          {
            guard: 'shouldNotifyOldVersionEditBlocked',
            actions: ['markOldVersionEditNoticeShown', emit({type: 'oldVersionEditBlocked'})],
          },
        ],
        'edit.discard': [
          {
            target: 'discarding',
            guard: 'hasPersistedDraft',
          },
          {
            actions: ['clearDraftState', 'pushPublishedContentToEditor'],
          },
        ],
        'document.remoteUpdate': {
          target: 'loaded',
          actions: ['setDocumentData', 'pushContentToEditor'],
          reenter: true,
        },
        'capability.changed': {
          actions: ['setCanEdit'],
        },
        'account.changed': {
          actions: ['setAccountIds'],
        },
        'version.changed': {
          actions: ['setRouteVersionState'],
        },
        'collection.query.change': {
          target: 'editing',
          guard: 'canTransitionToEditing',
          actions: ['setDepsFromPublished', 'snapshotBaseBlocks', 'updateCollectionQuery'],
        },
        'collection.convertToCollection': {
          target: 'editing',
          guard: 'canTransitionToEditing',
          actions: ['setDepsFromPublished', 'snapshotBaseBlocks', 'convertToCollection', raise({type: 'change'})],
        },
        'collection.convertToDocument': {
          target: 'editing',
          guard: 'canTransitionToEditing',
          actions: ['setDepsFromPublished', 'snapshotBaseBlocks', 'convertToDocument', raise({type: 'change'})],
        },
        'draft.existing': [
          {
            target: 'editing',
            guard: 'canOpenExistingDraft',
            actions: ['setExistingDraft', 'clearShouldAutoEdit', 'setDepsFromPublished', 'snapshotBaseBlocks'],
          },
          {
            actions: ['setExistingDraft'],
          },
        ],
      },
      always: [
        {
          target: 'editing',
          guard: ({context}) =>
            context.shouldAutoEdit &&
            context.canEdit &&
            shouldAllowDraftOverlay(context.isLatestVersion, context.routeVersion) &&
            (context.isLatestVersion || !!context.draftId),
          actions: ['clearShouldAutoEdit', 'setDepsFromPublished', 'snapshotBaseBlocks'],
        },
      ],
      states: {
        rename: renameRegion(),
      },
    },

    editing: {
      type: 'parallel',
      entry: [
        'logEnterEditing',
        {type: 'setEditorEditable'},
        enqueueActions(({event, enqueue}) => {
          // Canceled or failed publication resumes the same live editing session.
          const eventType: string = event.type
          if (eventType === 'publish.cancelChildDeletion' || eventType === 'xstate.error.actor.publishDocument') return
          enqueue('applyInitialContentToEditor')
          enqueue('placeCursorFromPendingOrDraft')
        }),
      ],
      exit: [
        () => {
          //console.log('[DocMachine] exit editing'),
        },
        {type: 'setEditorReadOnly'},
      ],
      on: {
        'collection.convertToCollection': {
          actions: ['convertToCollection', raise({type: 'change'})],
        },
        'collection.convertToDocument': {
          actions: ['convertToDocument', raise({type: 'change'})],
        },
        // Already editing: only move the cursor when the event explicitly asks
        // for a position (e.g. the click-below-content affordance sends 'end').
        // A bare edit.start re-entry must not clobber the user's current
        // selection (e.g. a node-selected block) with a stale draft cursor.
        'edit.start': {
          guard: ({event}) => event.cursorPosition != null,
          actions: ['setPendingEditCursorPosition', 'placeCursorFromPendingOrDraft'],
        },
        'edit.cancel': {
          target: 'loaded',
          actions: [() => console.log('[DocMachine] edit.cancel received in editing → loaded'), 'clearEditingState'],
        },
        'edit.discard': [
          {
            target: 'discarding',
            guard: 'hasPersistedDraft',
            actions: [() => console.log('[DocMachine] edit.discard received in editing → discarding')],
          },
          {
            target: 'loaded',
            actions: ['clearDraftState', 'pushPublishedContentToEditor'],
          },
        ],
        'capability.changed': [
          {
            target: 'loaded',
            guard: 'capabilityLost',
            actions: ['setCanEdit', 'clearEditingState'],
          },
          {
            actions: ['setCanEdit'],
          },
        ],
        'account.changed': {
          actions: ['setAccountIds'],
        },
        'document.remoteUpdate': {
          actions: ['logRemoteUpdateWhileEditing', 'setPendingRemoteDocument'],
        },
        'document.loaded': {
          actions: ['setDocumentData', 'markDocumentReady'],
        },
        'version.changed': {
          actions: ['setRouteVersionState'],
        },
        'rebase.blockTouched': {
          actions: ['appendMineTouched'],
        },
      },
      states: {
        rename: renameRegion(),
        draft: {
          initial: 'idle',
          on: {
            'change.navigation': [
              {
                target: '.saving',
                guard: 'hasPersistedDraft',
                actions: ['setNavigation'],
              },
              {
                target: '.creating',
                actions: ['setNavigation'],
              },
            ],
          },
          states: {
            idle: {
              on: {
                'collection.query.change': {
                  target: 'changed',
                  actions: ['updateCollectionQuery'],
                },
                change: {
                  target: 'changed',
                  actions: ['setMetadata'],
                },
                'rootChildrenType.change': {
                  target: 'changed',
                  actions: ['setMetadata'],
                },
                'reset.content': {
                  target: 'changed',
                },
                'publish.start': {
                  target: '#DocumentLifecycle.publishing',
                  guard: 'hasPersistedDraftAndNoRebaseConflict',
                  actions: ['setPathOverrideFromEvent'],
                },
                'version.changed': [
                  {
                    target: '#DocumentLifecycle.loaded',
                    guard: 'routeDisallowsDraftOverlay',
                    actions: [
                      'setRouteVersionState',
                      'promotePendingRemoteDocument',
                      'clearEditingState',
                      'pushContentToEditor',
                    ],
                  },
                  {
                    actions: ['setRouteVersionState'],
                  },
                ],
                // A `publish.start` queued during a previous saving/creating
                // raises `_save.completed` from the actor's onDone, lands here,
                // and immediately flushes into `publishing`.
                '_save.completed': {
                  target: '#DocumentLifecycle.publishing',
                  guard: 'hasPendingPublish',
                },
              },
            },
            changed: {
              on: {
                'collection.query.change': {
                  target: 'changed',
                  actions: ['updateCollectionQuery'],
                  reenter: true,
                },
                change: {
                  target: 'changed',
                  actions: ['setMetadata'],
                  reenter: true,
                },
                'rootChildrenType.change': {
                  target: 'changed',
                  actions: ['setMetadata'],
                  reenter: true,
                },
                'reset.content': {
                  target: 'changed',
                  reenter: true,
                },
                // Publish during the autosave debounce window: skip the timer,
                // jump straight into saving/creating, and queue the publish to
                // fire once the save completes.
                'publish.start': [
                  {
                    target: 'saving',
                    guard: 'hasPersistedDraftAndNoRebaseConflict',
                    actions: ['markPendingPublish', 'setPathOverrideFromEvent'],
                  },
                  {
                    target: 'creating',
                    guard: 'noRebaseConflict',
                    actions: ['markPendingPublish', 'setPathOverrideFromEvent'],
                  },
                ],
                'version.changed': [
                  {
                    target: 'saving',
                    guard: ({context, event}) =>
                      context.draftId !== null &&
                      context.draftCreated &&
                      event.type === 'version.changed' &&
                      !shouldAllowDraftOverlay(event.isLatest, event.routeVersion ?? context.routeVersion),
                    actions: ['setRouteVersionState', 'markPendingExitEditingAfterSave'],
                  },
                  {
                    target: 'creating',
                    guard: 'routeDisallowsDraftOverlay',
                    actions: ['setRouteVersionState', 'markPendingExitEditingAfterSave'],
                  },
                  {
                    actions: ['setRouteVersionState'],
                  },
                ],
              },
              after: {
                autosaveTimeout: [
                  {
                    target: 'saving',
                    guard: 'hasPersistedDraft',
                  },
                  {
                    target: 'creating',
                  },
                ],
              },
            },
            creating: {
              entry: ['logSaveStarted', 'resetChangeWhileSaving', raise({type: '_save.started'})],
              on: {
                'collection.query.change': {
                  actions: ['setHasChangedWhileSaving', 'updateCollectionQuery'],
                },
                change: {
                  actions: ['setHasChangedWhileSaving', 'setMetadata'],
                },
                'rootChildrenType.change': {
                  actions: ['setHasChangedWhileSaving', 'setMetadata'],
                },
                'reset.content': {
                  actions: ['setHasChangedWhileSaving'],
                },
                // Queue the publish; flushed when the in-flight create resolves
                // and onDone raises `_save.completed` in idle.
                'publish.start': {
                  guard: 'noRebaseConflict',
                  actions: ['markPendingPublish', 'setPathOverrideFromEvent'],
                },
                'version.changed': [
                  {
                    guard: 'routeDisallowsDraftOverlay',
                    actions: ['setRouteVersionState', 'markPendingExitEditingAfterSave'],
                  },
                  {
                    actions: ['setRouteVersionState'],
                  },
                ],
              },
              invoke: {
                id: 'writeDraft',
                src: 'writeDraft',
                input: ({context}) => ({
                  draftId: context.draftId,
                  metadata: context.metadata,
                  deps: context.deps,
                  maintenanceRevision: context.maintenanceRevision,
                  navigation: context.navigation,
                  locationUid: context.locationUid,
                  locationPath: context.locationPath,
                  editUid: context.editUid,
                  editPath: context.editPath,
                  signingAccountId: context.signingAccountId,
                  removedChildDocumentIds: context.removedChildDocumentIds,
                  mineTouchedIds: context.mineTouchedIds,
                  // Fall back to the published document body so an
                  // attributes-only edit (no content editor mounted) preserves
                  // the existing content instead of persisting an empty draft
                  // body that blanks the Content tab and wipes content on publish.
                  baseBlocks: context.baseBlocks ?? context.document?.content ?? null,
                  publishPath: context.publishPath,
                  schemaDraft: context.schemaDraft,
                  bindingSchemaDrafts: context.bindingSchemaDrafts,
                  contentOverride: getMachineOwnedContentOverride(context),
                }),
                onDone: [
                  {
                    target: 'saving',
                    guard: 'didChangeWhileSaving',
                    actions: [
                      'setDraftCreated',
                      {
                        type: 'setDraftIdFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      {
                        type: 'setDraftSavedSnapshotFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      'logSaveCompleted',
                    ],
                    reenter: true,
                  },
                  {
                    target: '#DocumentLifecycle.loaded',
                    guard: 'hasPendingExitEditingAfterSave',
                    actions: [
                      'setDraftCreated',
                      {
                        type: 'setDraftIdFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      {
                        type: 'setDraftSavedSnapshotFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      'logSaveCompleted',
                      'promotePendingRemoteDocument',
                      'clearEditingState',
                      'pushContentToEditor',
                      raise({type: '_save.completed'}),
                    ],
                  },
                  {
                    target: 'idle',
                    actions: [
                      'setDraftCreated',
                      {
                        type: 'setDraftIdFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      {
                        type: 'setDraftSavedSnapshotFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      'logSaveCompleted',
                      raise({type: '_save.completed'}),
                    ],
                  },
                ],
                onError: {
                  target: 'idle',
                  actions: [
                    ({event}: {event: any}) => {
                      documentEmbedCleanupError(
                        `${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine draft create failed`,
                        {
                          error: event.error,
                        },
                      )
                    },
                    raise({type: '_save.completed'}),
                  ],
                },
              },
            },
            saving: {
              entry: ['logSaveStarted', 'resetChangeWhileSaving', raise({type: '_save.started'})],
              on: {
                'collection.query.change': {
                  actions: ['setHasChangedWhileSaving', 'updateCollectionQuery'],
                },
                change: {
                  actions: ['setHasChangedWhileSaving', 'setMetadata'],
                },
                'rootChildrenType.change': {
                  actions: ['setHasChangedWhileSaving', 'setMetadata'],
                },
                'reset.content': {
                  actions: ['setHasChangedWhileSaving'],
                },
                // Same flush semantics as `creating`. Repeated clicks are
                // idempotent thanks to `markPendingPublish` being a constant
                // assign.
                'publish.start': {
                  guard: 'noRebaseConflict',
                  actions: ['markPendingPublish', 'setPathOverrideFromEvent'],
                },
                'version.changed': [
                  {
                    guard: 'routeDisallowsDraftOverlay',
                    actions: ['setRouteVersionState', 'markPendingExitEditingAfterSave'],
                  },
                  {
                    actions: ['setRouteVersionState'],
                  },
                ],
              },
              invoke: {
                id: 'writeDraft',
                src: 'writeDraft',
                input: ({context}) => ({
                  draftId: context.draftId,
                  metadata: context.metadata,
                  deps: context.deps,
                  maintenanceRevision: context.maintenanceRevision,
                  navigation: context.navigation,
                  locationUid: context.locationUid,
                  locationPath: context.locationPath,
                  editUid: context.editUid,
                  editPath: context.editPath,
                  signingAccountId: context.signingAccountId,
                  removedChildDocumentIds: context.removedChildDocumentIds,
                  mineTouchedIds: context.mineTouchedIds,
                  // Fall back to the published document body so an
                  // attributes-only edit (no content editor mounted) preserves
                  // the existing content instead of persisting an empty draft
                  // body that blanks the Content tab and wipes content on publish.
                  baseBlocks: context.baseBlocks ?? context.document?.content ?? null,
                  publishPath: context.publishPath,
                  schemaDraft: context.schemaDraft,
                  bindingSchemaDrafts: context.bindingSchemaDrafts,
                  contentOverride: getMachineOwnedContentOverride(context),
                }),
                onDone: [
                  {
                    target: 'saving',
                    guard: 'didChangeWhileSaving',
                    actions: [
                      {
                        type: 'setDraftSavedSnapshotFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                    ],
                    reenter: true,
                  },
                  {
                    target: '#DocumentLifecycle.loaded',
                    guard: 'hasPendingExitEditingAfterSave',
                    actions: [
                      {
                        type: 'setDraftSavedSnapshotFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      'logSaveCompleted',
                      'promotePendingRemoteDocument',
                      'clearEditingState',
                      'pushContentToEditor',
                      raise({type: '_save.completed'}),
                    ],
                  },
                  {
                    target: 'idle',
                    actions: [
                      {
                        type: 'setDraftSavedSnapshotFromResult',
                        params: ({event}: {event: any}) => event.output,
                      },
                      'logSaveCompleted',
                      raise({type: '_save.completed'}),
                    ],
                  },
                ],
                onError: [
                  {target: 'saving', guard: 'didChangeWhileSaving', reenter: true},
                  {
                    target: 'idle',
                    actions: [
                      ({event}: {event: any}) => {
                        documentEmbedCleanupError(
                          `${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine draft save failed`,
                          {
                            error: event.error,
                          },
                        )
                      },
                      raise({type: '_save.completed'}),
                    ],
                  },
                ],
              },
            },
          },
        },
        saveIndicator: {
          initial: 'hidden',
          states: {
            hidden: {
              on: {
                '_save.started': {
                  target: 'saving',
                },
              },
            },
            saving: {
              on: {
                '_save.completed': {
                  target: 'saved',
                },
              },
            },
            saved: {
              after: {
                saveIndicatorDismiss: {
                  target: 'hidden',
                },
              },
              on: {
                '_save.started': {
                  target: 'saving',
                },
              },
            },
          },
        },
        // Rebase region: tracks whether an incoming remote update has been
        // classified as a conflict awaiting Phase B resolution. The actual
        // classification + auto-merge happens in `useAutoRebase` (React layer);
        // the machine just records the resulting state so guards (like
        // publish.start) and selectors can read it explicitly.
        rebase: {
          initial: 'idle',
          states: {
            idle: {
              entry: () => {
                //console.log('[Rebase machine] region.idle entered')
              },
              on: {
                'rebase.detectConflict': {
                  target: 'conflict',
                  actions: ['setRebaseConflict'],
                },
                // Auto-merge applied from idle stays in idle; the action
                // updates context (document/deps/baseBlocks) and clears
                // pending fields.
                'rebase.apply': {
                  actions: ['applyRebaseMerge'],
                },
              },
            },
            conflict: {
              entry: () => {
                //console.log('[Rebase machine] region.conflict entered')
              },
              on: {
                'rebase.apply': {
                  target: 'idle',
                  actions: ['applyRebaseMerge'],
                },
                'rebase.dismiss': {
                  target: 'idle',
                  actions: ['clearPendingRemoteUpdate'],
                },
              },
            },
          },
        },
      },
    },

    discarding: {
      invoke: {
        id: 'discardDraft',
        src: 'discardDraft',
        input: ({context}) => ({
          draftId: context.draftId!,
          deletedChildDraftIds: context.pendingDeletedChildDraftIds,
        }),
        onDone: {
          target: 'loaded',
          actions: ['clearDraftState', 'pushPublishedContentToEditor'],
        },
        onError: {
          target: 'editing',
          actions: ({event}: {event: any}) => {
            documentEmbedCleanupError(`${DOCUMENT_EMBED_CLEANUP_LOG_PREFIX} document machine draft discard failed`, {
              error: event.error,
            })
          },
        },
      },
    },

    publishing: {
      initial: 'checkingChildDeletion',
      entry: ['clearPendingPublish', assign({confirmedChildDeletions: [], childDeletionError: null})],
      exit: assign({confirmedChildDeletions: [], childDeletionError: null}),
      states: {
        checkingChildDeletion: {
          always: [
            {
              guard: ({context}) =>
                !!context.publishedVersion &&
                !hasSelfQueryBlock(getChildDeletionContent(context), context.documentId.id) &&
                (hasSelfQueryBlock(context.baseBlocks ?? context.document?.content ?? [], context.documentId.id) ||
                  getChildDeletionCandidates(context).length > 0 ||
                  context.removedChildDocumentIds.length > 0 ||
                  getRemovedChildReferenceTargets(
                    context.documentId,
                    context.baseBlocks ?? context.document?.content ?? [],
                    getChildDeletionContent(context),
                  ).length > 0),
              target: 'inspectingChildDeletion',
            },
            {target: 'inProgress'},
          ],
        },
        inspectingChildDeletion: {
          invoke: {
            src: 'inspectChildDeletions',
            input: ({context}) => ({
              parentId: context.documentId,
              childIds: getChildDeletionCandidates(context),
              removedSelfQuery:
                hasSelfQueryBlock(context.baseBlocks ?? context.document?.content ?? [], context.documentId.id) &&
                !hasSelfQueryBlock(getChildDeletionContent(context), context.documentId.id),
              removedReferenceTargets: Array.from(
                new Set([
                  ...context.removedChildDocumentIds,
                  ...getRemovedChildReferenceTargets(
                    context.documentId,
                    context.baseBlocks ?? context.document?.content ?? [],
                    getChildDeletionContent(context),
                  ),
                ]),
              ),
              content: getChildDeletionContent(context),
            }),
            onDone: [
              {
                guard: ({event}) => event.output.length > 0,
                target: 'confirmingChildDeletion',
                actions: assign({confirmedChildDeletions: ({event}) => event.output}),
              },
              {target: 'inProgress'},
            ],
            onError: {
              target: 'childDeletionFailed',
              actions: assign({
                childDeletionError: ({event}) =>
                  event.error instanceof Error ? event.error.message : String(event.error),
              }),
            },
          },
        },
        confirmingChildDeletion: {
          on: {
            'publish.confirmChildDeletion': {target: 'inProgress'},
            'publish.cancelChildDeletion': {
              target: '#DocumentLifecycle.editing.draft.idle',
              actions: ['clearPathOverride'],
            },
          },
        },
        childDeletionFailed: {
          on: {
            'publish.cancelChildDeletion': {
              target: '#DocumentLifecycle.editing.draft.idle',
              actions: ['clearPathOverride'],
            },
          },
        },
        inProgress: {
          invoke: {
            id: 'publishDocument',
            src: 'publishDocument',
            input: ({context}) => ({
              documentId: context.documentId,
              draftId: context.draftId!,
              deps: context.deps,
              metadata: context.metadata,
              navigation: context.navigation,
              publishAccountUid: context.publishAccountUid,
              pathOverride: context.pendingPathOverride ?? undefined,
              deletedChildDraftIds: context.pendingDeletedChildDraftIds,
              confirmedChildDeletions: context.confirmedChildDeletions,
            }),
            onDone: {
              target: 'cleaningUp',
            },
            onError: {
              target: '#DocumentLifecycle.editing.draft.idle',
              actions: ['clearPathOverride', {type: 'notifyPublishError', params: ({event}) => ({error: event.error})}],
            },
          },
        },
        cleaningUp: {
          entry: [
            'clearDraftState',
            'updatePublishedVersion',
            // Fire-and-forget push to destination servers. The spawned child runs
            // independently of the machine's state transitions; its outcome is
            // surfaced via toast in the consumer-provided actor.
            spawnChild('pushDocument', {
              input: ({event}) => ({
                publishedDocument: (event as any).output as HMDocument,
              }),
            }),
          ],
          always: {
            target: '#DocumentLifecycle.loaded',
          },
        },
      },
    },

    error: {
      on: {
        'document.retry': {
          target: 'loading',
          actions: [assign({error: null})],
        },
        'capability.changed': {
          actions: ['setCanEdit'],
        },
        'account.changed': {
          actions: ['setAccountIds'],
        },
        'version.changed': {
          actions: ['setRouteVersionState'],
        },
      },
    },
  },
})
